import { useState, useEffect, useMemo, useRef } from 'react';
import { X, BookOpen, CheckCircle2, Mail, Camera, XCircle, Clock, AlertTriangle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/input';
import { Dropdown } from '../ui/Dropdown';
import { ProfessorEditSidePanel } from './ProfessorEditSidePanel';
import type { Professor, Materia } from '../../types/models';
import { formatCPF, validateCPF, stripMask } from '../../utils/cpfUtils';
import '../../components/ui/datatable.css';
import { ROUTES, UPLOAD } from '../../api/routes';
import { InviteResultDialog } from '../invite';
import { AvatarCropper } from '../ui/AvatarCropper';

const STEPS = ['Dados Pessoais', 'Disciplinas', 'Convite'];

/** Props do formulario multi-step de professor */
export interface ProfessorFormProps {
  open: boolean;
  onClose: () => void;
  editing?: Professor | null;
  onCreated?: (id: string) => void;
}

const emptyForm = { nome: '', email: '', cpf: '', telefone: '', especialidade: '', fotoUrl: '' };

function formatTelefone(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function ProfessorForm({ open, onClose, editing, onCreated }: ProfessorFormProps) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...emptyForm });
  const { data: professores, create, update } = useCrud<Professor>('professores');
  const { data: materias } = useCrud<Materia>('materias');
  const [selectedMaterias, setSelectedMaterias] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const escolaId = useAuthStore((s) => s.user?.escolaId);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [createdProfId, setCreatedProfId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const stepScrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    modalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    stepScrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const changeStep = (newStep: number) => {
    setStep(newStep);
    setTimeout(scrollToTop, 40);
  };

  const [fotoUploading, setFotoUploading] = useState(false);
  const [pendingFotoBase64, setPendingFotoBase64] = useState<string | null>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  // Resultado do convite (link de ativacao)
  const [conviteResult, setConviteResult] = useState<{
    email: string;
    activationLink: string;
    nome: string;
    materiasVinculadas: string[];
    emailEnviado?: boolean;
    emailMessageId?: string;
    convidadoPor?: string;
  } | null>(null);

  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null);

  const avatarInitial = useMemo(() => {
    if (!form.nome) return '?';
    const parts = form.nome.trim().split(/\s+/);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [form.nome]);

  const onAvatarFilePicked: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem.');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx. 10 MB).');
      return;
    }
    setAvatarCropFile(f);
  };

  const handleAvatarCropped = async (base64: string) => {
    setAvatarCropFile(null);
    if (!base64) return;

    if (!editing?.id) {
      setPendingFotoBase64(base64);
      setForm(prev => ({ ...prev, fotoUrl: base64 }));
    } else {
      setFotoUploading(true);
      try {
        const { data } = await api.post<{ success: boolean; url: string }>(UPLOAD.fotoUsuario(editing.id), { fotoBase64: base64 });
        setForm(prev => ({ ...prev, fotoUrl: data.url }));
        editing.fotoUrl = data.url;
        toast.success('Foto atualizada com sucesso.');
      } catch (err) {
        toast.error('Erro ao fazer upload da foto.');
      } finally {
        setFotoUploading(false);
      }
    }
  };

  useEffect(() => {
    if (editing) {
      setForm({
        nome: editing.nome || '',
        email: editing.email || '',
        cpf: formatCPF(editing.cpf || ''),
        telefone: formatTelefone(editing.telefone || ''),
        especialidade: editing.especialidade || '',
        fotoUrl: editing.fotoUrl || '',
      });
      setSelectedMaterias(editing.materias?.map(m => m.id) || []);
    } else {
      setForm({ ...emptyForm });
      setSelectedMaterias([]);
    }
    setStep(0);
    setConviteResult(null);
    setFieldErrors({});
    setCreatedProfId(null);
    setPendingFotoBase64(null);
  }, [editing, open]);

  const set = (k: string, v: string) => {
    setForm(prev => ({ ...prev, [k]: v }));
    // Limpa erro do campo ao digitar
    if (fieldErrors[k]) setFieldErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
  };

  /** Valida campos obrigatórios do Step 1 antes de avançar */
  const validateStep1Async = async (): Promise<boolean> => {
    const errors: Record<string, string> = {};

    // 1. Nome
    if (!form.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    } else if (form.nome.trim().length < 3) {
      errors.nome = 'Nome deve ter ao menos 3 caracteres';
    }

    // 2. Email
    const cleanEmail = form.email.trim().toLowerCase();
    if (!cleanEmail) {
      errors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      errors.email = 'E-mail inválido';
    } else {
      const emailDup = professores?.find(
        (p) => p.email?.toLowerCase() === cleanEmail && p.id !== editing?.id,
      );
      if (emailDup) {
        errors.email = `E-mail já cadastrado para o professor ${emailDup.nome}`;
      } else {
        try {
          const { data } = await api.get(ROUTES.validacoes.email(cleanEmail));
          if (data.exists && (!editing || data.id !== editing.id)) {
            errors.email = `Este e-mail já está em uso pelo ${
              data.tipo === 'RESPONSAVEL'
                ? 'Responsável'
                : data.tipo === 'ALUNO'
                  ? 'Aluno'
                  : 'Usuário'
            }: ${data.nome}`;
          }
        } catch {
          // fallback silencioso
        }
      }
    }

    // 3. CPF (obrigatório + validação de dígitos + duplicidade)
    const cpfDigits = stripMask(form.cpf);
    if (!cpfDigits) {
      errors.cpf = 'CPF é obrigatório';
    } else if (cpfDigits.length !== 11) {
      errors.cpf = 'CPF deve ter 11 dígitos';
    } else if (!validateCPF(cpfDigits)) {
      errors.cpf = 'CPF inválido (dígitos verificadores)';
    } else {
      const cpfDup = professores?.find(
        (p) => p.cpf && stripMask(p.cpf) === cpfDigits && p.id !== editing?.id,
      );
      if (cpfDup) {
        errors.cpf = `CPF já cadastrado para o professor ${cpfDup.nome}`;
      } else {
        try {
          const { data } = await api.get(ROUTES.validacoes.cpf(cpfDigits));
          if (data.exists && (!editing || (data.id !== editing.id && data.nome !== editing.nome))) {
            errors.cpf = `Este CPF já está em uso pelo ${
              data.tipo === 'RESPONSAVEL'
                ? 'Responsável'
                : data.tipo === 'ALUNO'
                  ? 'Aluno'
                  : 'Usuário'
            }: ${data.nome}`;
          }
        } catch {
          // fallback silencioso
        }
      }
    }

    // 4. Telefone (obrigatório + mínimo 10 dígitos)
    const telDigits = form.telefone.replace(/\D/g, '');
    if (!telDigits) {
      errors.telefone = 'Telefone é obrigatório';
    } else if (telDigits.length < 10) {
      errors.telefone = 'Telefone deve ter ao menos 10 dígitos com DDD';
    } else {
      const telDup = professores?.find(
        (p) => p.telefone && p.telefone.replace(/\D/g, '') === telDigits && p.id !== editing?.id,
      );
      if (telDup) {
        errors.telefone = `Telefone já cadastrado (${telDup.nome})`;
      }
    }

    // 5. Especialidade (obrigatório)
    if (!form.especialidade || !form.especialidade.trim()) {
      errors.especialidade = 'Especialidade é obrigatória';
    }

    setFieldErrors(errors);

    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const firstMsg = errors[errorKeys[0]];
      toast.warning(firstMsg);
      return false;
    }

    return true;
  };

  const checkEmailLive = async (emailVal: string) => {
    const cleanEmail = emailVal.trim().toLowerCase();
    if (!cleanEmail) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setFieldErrors((prev) => ({ ...prev, email: 'E-mail inválido' }));
      return;
    }
    const emailDup = professores?.find(
      (p) => p.email?.toLowerCase() === cleanEmail && p.id !== editing?.id,
    );
    if (emailDup) {
      const msg = `Este e-mail já está cadastrado para o professor ${emailDup.nome}.`;
      setFieldErrors((prev) => ({ ...prev, email: msg }));
      toast.warning(msg);
      return;
    }
    try {
      const { data } = await api.get(ROUTES.validacoes.email(cleanEmail));
      if (data.exists && (!editing || data.id !== editing.id)) {
        const msg = `Este e-mail já está em uso pelo ${
          data.tipo === 'RESPONSAVEL'
            ? 'Responsável'
            : data.tipo === 'ALUNO'
              ? 'Aluno'
              : 'Usuário'
        }: ${data.nome}.`;
        setFieldErrors((prev) => ({ ...prev, email: msg }));
        toast.warning(msg);
      }
    } catch (err) {
      console.error('Erro validando e-mail', err);
    }
  };

  const checkCpfLive = async (cpfVal: string) => {
    const cleanCpf = stripMask(cpfVal);
    if (cleanCpf.length === 11 && validateCPF(cpfVal)) {
      const cpfDup = professores?.find(
        (p) => p.cpf && stripMask(p.cpf) === cleanCpf && p.id !== editing?.id,
      );
      if (cpfDup) {
        const msg = `Este CPF já está cadastrado para o professor ${cpfDup.nome}.`;
        setFieldErrors((prev) => ({ ...prev, cpf: msg }));
        toast.warning(msg);
        return;
      }
      try {
        const { data } = await api.get(ROUTES.validacoes.cpf(cleanCpf));
        if (data.exists) {
          if (editing && (data.id === editing.id || data.nome === editing.nome)) return;
          const msg = `Este CPF já está em uso pelo ${
            data.tipo === 'RESPONSAVEL'
              ? 'Responsável'
              : data.tipo === 'ALUNO'
                ? 'Aluno'
                : 'Usuário'
          }: ${data.nome}.`;
          setFieldErrors((prev) => ({ ...prev, cpf: msg }));
          toast.warning(msg);
        }
      } catch (err) {
        console.error('Erro validando CPF', err);
      }
    }
  };

  const materiasVisiveis = useMemo(() => {
    const byId = new Map<string, { id: string; nome: string; ativo: boolean; corVisual?: string }>();

    // 1) Matérias ativas vindas da lista geral
    for (const m of materias) {
      if (!m.ativo) continue;
      byId.set(m.id, { id: m.id, nome: m.nome, ativo: !!m.ativo, corVisual: m.corVisual });
    }

    // 2) Garante que matérias já vinculadas ao professor apareçam no formulário,
    // mesmo se não vierem na listagem de matérias da escola.
    for (const m of (editing?.materias ?? [])) {
      if (!m?.id || !m?.nome) continue;
      if (!byId.has(m.id)) {
        byId.set(m.id, { id: m.id, nome: m.nome, ativo: false, corVisual: (m as any).corVisual });
      }
    }

    return Array.from(byId.values()).sort((a, b) => {
      if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
      return a.nome.localeCompare(b.nome, 'pt-BR');
    });
  }, [materias, editing?.materias, selectedMaterias]);

  const especialidadeOptions = useMemo(() => {
    const list = materias ? materias.filter(m => m.ativo) : [];
    return list.map(m => ({
      value: m.nome,
      label: m.nome,
    }));
  }, [materias]);

  const toggleMateria = (id: string) => {
    setSelectedMaterias(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  const handleSubmit = async () => {
    if (selectedMaterias.length === 0) {
      toast.error('Selecione pelo menos uma disciplina para o professor.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { fotoUrl, ...payloadForm } = form;
        await update.mutateAsync({ id: editing.id, ...payloadForm } as any);

        // Sincronizar disciplinas selecionadas (compatível com backend atual)
        if (escolaId) {
          const { data: vinculosAtuais } = await api.get(ROUTES.professores.materias(escolaId, editing.id));
          const lista = Array.isArray(vinculosAtuais) ? vinculosAtuais : [];
          const atuais = lista
            .map((v: any) => ({ vinculoId: v.id as string, materiaId: v.materiaId as string }))
            .filter((v: any) => v.vinculoId && v.materiaId);

          const atuaisIds = new Set(atuais.map((a: any) => a.materiaId));
          const selecionadasIds = new Set(selectedMaterias);

          // Remover vínculos não selecionados
          for (const v of atuais) {
            if (!selecionadasIds.has(v.materiaId)) {
              await api.delete(ROUTES.professores.removerMateria(escolaId, editing.id, v.vinculoId));
            }
          }

          // Criar vínculos faltantes
          for (const materiaId of selectedMaterias) {
            if (!atuaisIds.has(materiaId)) {
              await api.post(ROUTES.professores.materias(escolaId, editing.id), { materiaId });
            }
          }
        }

        await queryClient.invalidateQueries({ queryKey: ['professores'] });

        toast.success('Professor atualizado!');
        onClose();
      } else {
        // Usar endpoint /convidar para gerar link de ativacao
        if (!escolaId) { toast.error('Escola nao identificada'); setSaving(false); return; }
        const { data: result } = await api.post(ROUTES.professores.listar(escolaId) + '/convidar', {
          nome: form.nome,
          email: form.email,
          cpf: form.cpf ? form.cpf.replace(/\D/g, '') : undefined,
          telefone: form.telefone || undefined,
          especialidade: form.especialidade || undefined,
          materiaIds: selectedMaterias.length > 0 ? selectedMaterias : undefined,
          acessoDireto: true,
        });

        // Buscar nomes das materias vinculadas
        const materiasAtivas = materias.filter(m => m.ativo);
        const nomesMaterias = selectedMaterias
          .map(id => materiasAtivas.find(m => m.id === id)?.nome)
          .filter(Boolean) as string[];

        const createdId = result.professorId || result.id;

        // Fazer upload da foto se houver uma pendente
        if (createdId && pendingFotoBase64) {
          try {
            await api.post(UPLOAD.fotoUsuario(createdId), { fotoBase64: pendingFotoBase64 });
          } catch (err) {
            // falha silenciosa
          }
        }

        // Invalidar cache para atualizar a lista
        queryClient.invalidateQueries({ queryKey: ['professores'] });
        // Professor já existia no sistema (outra escola) → apenas vinculou
        if (result.vinculoExistente) {
          toast.success(`Professor ${form.nome} já existia no sistema e foi vinculado a esta escola!`);
          onClose();
          queryClient.invalidateQueries({ queryKey: ['professores'] });
          window.dispatchEvent(new CustomEvent('ac-data-refetch'));
          if (createdId && onCreated) setTimeout(() => onCreated(createdId), 150);
          return;
        }

        // Reconstruir link com a origem atual para evitar erro de porta (Vite port drift)
        let activationLink = result.activationLink || '';
        try {
          const parsed = new URL(activationLink);
          activationLink = `${window.location.origin}${parsed.pathname}${parsed.search}`;
        } catch { /* mantém link do backend como fallback */ }

        setConviteResult({
          email: form.email,
          activationLink,
          nome: form.nome,
          materiasVinculadas: nomesMaterias,
          emailEnviado: result.emailEnviado || false,
          emailMessageId: result.emailMessageId || undefined,
          convidadoPor: result.convidadoPor || undefined,
        });

        queryClient.invalidateQueries({ queryKey: ['professores'] });
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));

        setStep(2); // Ir para tela de convite
        if (createdId) setCreatedProfId(createdId);
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || '';
      const msgLower = msg.toLowerCase();

      // Mapear erros comuns para mensagens claras
      if (msgLower.includes('cpf já está em uso')) {
        toast.error(msg);
        setFieldErrors(prev => ({ ...prev, cpf: msg }));
        setStep(0);
      } else if (status === 409 || msgLower.includes('vinculado') || msgLower.includes('já existe') || msgLower.includes('conflict')) {
        toast.error('Este professor já está cadastrado nesta escola. Verifique a lista de professores ou use outro e-mail.');
      } else if (status === 400 && msgLower.includes('email')) {
        toast.error('E-mail inválido. Verifique o endereço e tente novamente.');
      } else if (status === 404) {
        toast.error('Escola não encontrada. Recarregue a página e tente novamente.');
      } else {
        toast.error(msg || 'Erro ao salvar professor. Tente novamente.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCloseConvite = () => {
    window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    onClose();
    setConviteResult(null);
    if (createdProfId && onCreated) onCreated(createdProfId);
  };

  // Determinar quantos steps mostrar no stepper
  // Edicao: apenas 2 steps (sem convite)
  const visibleSteps = editing ? STEPS.slice(0, 2) : STEPS;
  const isLastFormStep = editing ? step === 1 : step === 1;

  if (!open) return null;

  const stepperUI = !conviteResult && (
    <div className="ac-professor-stepper" style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 12 }}>
      {visibleSteps.map((label, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < visibleSteps.length - 1 ? 1 : 'none' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.7rem', fontWeight: 700,
              background: i < step ? '#16a34a' : i === step ? '#1B365D' : '#e5e7eb',
              color: i <= step ? 'white' : '#9ca3af',
            }}>{i < step ? '✓' : i + 1}</span>
            <span style={{ fontSize: '0.66rem', fontWeight: 600, color: i === step ? '#1B365D' : '#9ca3af' }}>{label}</span>
          </div>
          {i < visibleSteps.length - 1 && (
            <div style={{ flex: 1, height: 2, background: i < step ? '#16a34a' : '#e5e7eb', margin: '0 12px', marginBottom: 18 }} />
          )}
        </div>
      ))}
    </div>
  );

  const formBodyUI = (
    <>
      {step <= 1 ? (
        <div ref={stepScrollContainerRef} style={{ maxHeight: 'min(480px, 65vh)', overflowY: 'auto', paddingRight: '4px', overflowX: 'hidden' }}>
          {/* Step 1: Dados Pessoais */}
          {step === 0 && (
            <div className="ac-professor-step flex flex-col sm:flex-row gap-4 sm:gap-6 items-center sm:items-start">
              {/* Foto Upload */}
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  className={`ac-equipe-avatar ${!editing ? 'ac-equipe-avatar--novo' : ''} ${fotoUploading ? 'ac-equipe-avatar--busy' : ''}`}
                  onClick={() => {
                    if (fotoUploading) return;
                    avatarFileInputRef.current?.click();
                  }}
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: '50%',
                    border: '2px dashed #cbd5e1',
                    background: '#f8fafc',
                    position: 'relative',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title={fotoUploading ? 'Enviando foto...' : (form.fotoUrl ? 'Clique para alterar a foto' : 'Clique para adicionar foto')}
                >
                  {form.fotoUrl ? (
                    <img src={form.fotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: '1.8rem', fontWeight: 700, color: '#64748b' }}>
                      {avatarInitial}
                    </span>
                  )}
                  {editing && !fotoUploading && (
                    <span className="ac-equipe-avatar__cam" style={{ position: 'absolute', bottom: 4, right: 4 }}>
                      <Camera size={10} />
                    </span>
                  )}
                </button>
                <input
                  ref={avatarFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={onAvatarFilePicked}
                />
                <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600 }}>Foto (Opcional)</span>
              </div>

              {/* Grid de campos */}
              <div className="w-full flex-1 grid grid-cols-1 sm:grid-cols-6 gap-3 sm:gap-3.5">
                <div className="form-group col-span-1 sm:col-span-6">
                  <label className="form-label">Nome Completo *</label>
                  <Input name="nome" data-testid="input-professor-nome" className={fieldErrors.nome ? 'border-red-500 ring-red-500/20' : ''} value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Nome do professor" />
                  {fieldErrors.nome && <span className="form-error">{fieldErrors.nome}</span>}
                </div>
                
                <div className="form-group col-span-1 sm:col-span-3">
                  <label className="form-label">E-mail *</label>
                  <Input
                    name="email"
                    data-testid="input-professor-email"
                    className={fieldErrors.email ? 'border-red-500 ring-red-500/20' : ''}
                    type="email"
                    value={form.email}
                    onChange={e => set('email', e.target.value)}
                    onBlur={() => checkEmailLive(form.email)}
                    placeholder="email@escola.edu.br"
                  />
                  {fieldErrors.email && <span className="form-error">{fieldErrors.email}</span>}
                </div>

                <div className="form-group col-span-1 sm:col-span-3">
                  <label className="form-label">CPF *</label>
                  <Input 
                    name="cpf"
                    data-testid="input-professor-cpf"
                    className={fieldErrors.cpf ? 'border-red-500 ring-red-500/20' : ''} 
                    value={form.cpf} 
                    onChange={e => set('cpf', formatCPF(e.target.value))} 
                    onBlur={() => checkCpfLive(form.cpf)}
                    placeholder="000.000.000-00" 
                    maxLength={14} 
                    inputMode="numeric" 
                  />
                  {fieldErrors.cpf && <span className="form-error">{fieldErrors.cpf}</span>}
                </div>

                <div className="form-group col-span-1 sm:col-span-3">
                  <label className="form-label">Telefone *</label>
                  <Input
                    name="telefone"
                    data-testid="input-professor-telefone"
                    className={fieldErrors.telefone ? 'border-red-500 ring-red-500/20' : ''}
                    value={form.telefone}
                    onChange={e => set('telefone', formatTelefone(e.target.value))}
                    placeholder="(00) 00000-0000"
                    inputMode="numeric"
                  />
                  {fieldErrors.telefone && <span className="form-error">{fieldErrors.telefone}</span>}
                </div>

                <div className="form-group col-span-1 sm:col-span-3">
                  <label className="form-label">Especialidade *</label>
                  <Dropdown
                    className="dropdown-especialidade"
                    options={especialidadeOptions}
                    value={form.especialidade}
                    onChange={(val) => set('especialidade', val as string)}
                    placeholder="Selecione..."
                    searchable
                    error={fieldErrors.especialidade}
                  />
                  {fieldErrors.especialidade && <span className="form-error">{fieldErrors.especialidade}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Vinculos / Materias */}
          {step === 1 && (
            <div className="ac-professor-step">
              <p style={{ fontSize: '0.74rem', color: '#6b7280' }}>Selecione as disciplinas que o professor leciona:</p>
              <div className="ac-professor-materias-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2.5 max-h-[260px] sm:max-h-[300px] overflow-y-auto p-1.5 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50 dark:bg-slate-900/40">
                {materiasVisiveis.map(m => {
                  const isSelected = selectedMaterias.includes(m.id);
                  const canToggle = m.ativo || isSelected; // inativa só pode desmarcar se já estava selecionada
                  return (
                    <label key={m.id} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
                      borderRadius: 8, border: `1px solid ${isSelected ? '#1B365D' : '#e5e7eb'}`,
                      background: isSelected ? '#f0f4ff' : '#f9fafb',
                      cursor: canToggle ? 'pointer' : 'not-allowed', transition: 'all 0.15s', fontSize: '0.78rem',
                      opacity: m.ativo ? 1 : 0.86,
                    }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!canToggle}
                        onChange={() => canToggle && toggleMateria(m.id)}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        border: `1.5px solid ${isSelected ? '#1b365d' : '#cbd5e1'}`,
                        background: isSelected ? '#1b365d' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 8,
                        flexShrink: 0,
                        transition: 'all 0.15s'
                      }}>
                        {isSelected && (
                          <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                      <span 
                        style={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          background: m.corVisual || '#cbd5e1', 
                          flexShrink: 0,
                          marginRight: 6
                        }} 
                      />
                      <span style={{ fontWeight: 600, color: 'var(--text-main, #374151)' }}>{m.nome}</span>
                      {!m.ativo && (
                        <span style={{ marginLeft: 'auto', fontSize: '0.62rem', color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 999, padding: '1px 6px', fontWeight: 700 }}>
                          Inativa
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {materiasVisiveis.length === 0 && (
                <p style={{ color: '#9ca3af', fontSize: '0.74rem' }}>Nenhuma materia cadastrada. Cadastre materias primeiro.</p>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Step 3: Convite Enviado (somente criacao) */
        step === 2 && conviteResult && (
          <InviteResultDialog
            open={true}
            nome={conviteResult.nome}
            email={conviteResult.email}
            perfil="PROFESSOR"
            activationLink={conviteResult.activationLink}
            emailSent={conviteResult.emailEnviado}
            onClose={handleCloseConvite}
          />
        )
      )}
    </>
  );

  const editModalFooter = (
    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
      <button 
        type="button" 
        style={{
          padding: '10px 20px',
          borderRadius: 10,
          fontSize: '0.85rem',
          fontWeight: 600,
          border: '1px solid #cbd5e1',
          background: 'white',
          color: '#475569',
          cursor: 'pointer',
        }}
        onClick={onClose}
      >
        Cancelar
      </button>
      
      <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
        {step > 0 && (
          <button 
            type="button" 
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              fontSize: '0.85rem',
              fontWeight: 600,
              border: '1px solid #cbd5e1',
              background: 'white',
              color: '#475569',
              cursor: 'pointer',
            }}
            onClick={() => changeStep(step - 1)}
          >
            ← Voltar
          </button>
        )}
        
        {isLastFormStep ? (
          <button 
            type="button" 
            onClick={() => void handleSubmit()} 
            disabled={saving}
            style={{
              padding: '10px 24px',
              borderRadius: 10,
              fontSize: '0.85rem',
              fontWeight: 600,
              border: 'none',
              background: saving ? '#a5b4fc' : '#10b981',
              color: 'white',
              cursor: saving ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
            }}
          >
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        ) : (
          <button
            type="button"
            onClick={async () => {
              if (step === 0) {
                const ok = await validateStep1Async();
                if (!ok) return;
              }
              changeStep(step + 1);
            }}
            style={{
              padding: '10px 24px',
              borderRadius: 10,
              fontSize: '0.85rem',
              fontWeight: 600,
              border: 'none',
              background: '#6366f1',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
            }}
          >
            Próximo →
          </button>
        )}
      </div>
    </div>
  );

  const createFooter = conviteResult ? (
    <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}>
      <button 
        type="button" 
        style={{
          padding: '10px 24px',
          borderRadius: 10,
          fontSize: '0.85rem',
          fontWeight: 600,
          border: '1px solid #cbd5e1',
          background: 'white',
          color: '#475569',
          cursor: 'pointer',
        }}
        onClick={handleCloseConvite}
      >
        Fechar
      </button>
    </div>
  ) : (
    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
      {step > 0 ? (
        <button 
          type="button" 
          style={{
            padding: '10px 20px',
            borderRadius: 10,
            fontSize: '0.85rem',
            fontWeight: 600,
            border: '1px solid #cbd5e1',
            background: 'white',
            color: '#475569',
            cursor: 'pointer',
          }}
          onClick={() => changeStep(step - 1)}
        >
          ← Voltar
        </button>
      ) : (
        <button 
          type="button" 
          style={{
            padding: '10px 20px',
            borderRadius: 10,
            fontSize: '0.85rem',
            fontWeight: 600,
            border: '1px solid #cbd5e1',
            background: 'white',
            color: '#475569',
            cursor: 'pointer',
          }}
          onClick={onClose}
        >
          Cancelar
        </button>
      )}
      
      {isLastFormStep ? (
        <button 
          type="button" 
          onClick={() => void handleSubmit()} 
          disabled={saving}
          style={{
            padding: '10px 24px',
            borderRadius: 10,
            fontSize: '0.85rem',
            fontWeight: 600,
            border: 'none',
            background: saving ? '#a5b4fc' : '#10b981',
            color: 'white',
            cursor: saving ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
          }}
        >
          {saving ? 'Salvando...' : 'Convidar Professor'}
        </button>
      ) : (
        <button 
          type="button" 
          onClick={async () => {
            if (step === 0) {
              const ok = await validateStep1Async();
              if (!ok) return;
            }
            changeStep(step + 1);
          }}
          style={{
            padding: '10px 24px',
            borderRadius: 10,
            fontSize: '0.85rem',
            fontWeight: 600,
            border: 'none',
            background: '#6366f1',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
          }}
        >
          Próximo →
        </button>
      )}
    </div>
  );

  if (editing && !conviteResult) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Editar Professor"
        size="wide"
        contentClassName="ac-professor-modal ac-professor-modal--edit"
        footer={editModalFooter}
      >
        <div className="ac-modal-dual ac-professor-edit-dual">
          <div className="ac-modal-dual__columns" style={{ flex: 1, minHeight: 0 }}>
            <div className="ac-modal-dual__main" style={{ overflow: 'visible', paddingRight: 4 }}>
              {stepperUI}
              {formBodyUI}
            </div>
            <div className="ac-modal-dual__divider ac-responsavel-modal-divider" aria-hidden />
            {editing.id && escolaId && (
              <ProfessorEditSidePanel
                escolaId={escolaId}
                professorId={editing.id}
                professor={editing}
                selectedMateriasCount={selectedMaterias.length}
              />
            )}
          </div>
        </div>
      </Modal>
    );
  }

  if (conviteResult) {
    return (
      <InviteResultDialog
        open={true}
        nome={conviteResult.nome}
        email={conviteResult.email}
        perfil="PROFESSOR"
        activationLink={conviteResult.activationLink}
        emailSent={conviteResult.emailEnviado}
        onClose={handleCloseConvite}
      />
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title=""
      size="lg"
      noBodyScroll
      contentClassName={`ac-professor-modal umu-wizard-modal ${conviteResult ? 'ac-professor-modal--convite' : ''}`}
    >
      {/* Banner de cabeçalho idêntico ao InviteMemberModal */}
      <div className="umu-wizard-banner" style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 40%, #3730a3 100%)',
        borderRadius: '12px 12px 0 0', padding: '14px 20px 12px', position: 'relative', overflow: 'hidden',
        flexShrink: 0,
        flex: '0 0 auto',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(99,102,241,.12)' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ color: 'white', fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
            {conviteResult ? 'Convite Enviado' : 'Novo Professor'}
          </h2>
        </div>

        {/* Stepper Inline integrado ao cabeçalho */}
        {!conviteResult && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
            {visibleSteps.map((s, i) => {
              const isCurrent = step === i;
              const isDone = step > i;
              return (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    onClick={async () => {
                      if (i === 0) changeStep(0);
                      else if (await validateStep1Async()) changeStep(1);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <div 
                      style={{ 
                        width: 22, 
                        height: 22, 
                        borderRadius: '50%', 
                        background: isCurrent ? 'white' : isDone ? '#10b981' : 'rgba(255,255,255,.15)', 
                        color: isCurrent ? '#1e40af' : isDone ? 'white' : 'rgba(255,255,255,.5)', 
                        fontSize: '.68rem', 
                        fontWeight: 700, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}
                    >
                      {isDone ? <CheckCircle2 size={12} /> : i + 1}
                    </div>
                    <span 
                      className={`${isCurrent ? 'inline' : 'hidden sm:inline'} text-[11px] sm:text-xs font-semibold whitespace-nowrap`}
                      style={{ color: isCurrent ? 'white' : 'rgba(255,255,255,.55)' }}
                    >
                      {s}
                    </span>
                  </button>
                  {i < visibleSteps.length - 1 && <div style={{ width: 24, height: 2, background: isDone ? '#10b981' : 'rgba(255,255,255,.15)', borderRadius: 2 }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div ref={modalBodyRef} className="umu-wizard-body" style={{ padding: '18px 20px 16px', flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
        {formBodyUI}

        {/* Adicionar a informação do Link de ativação seguro - Oculto no mobile */}
        {!editing && !conviteResult && (
          <div className="hidden sm:flex" style={{
            alignItems: 'flex-start',
            gap: 12,
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(99, 102, 241, 0.05)',
            border: '1px solid rgba(99, 102, 241, 0.12)',
            marginTop: 16,
          }}>
            <Mail size={16} style={{ color: '#6366f1', flexShrink: 0, marginTop: 2 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontWeight: 700, fontSize: '.84rem', color: '#312e81' }}>Link de ativação seguro</div>
              <div style={{ fontSize: '.76rem', color: '#4338ca', lineHeight: 1.35 }}>
                O professor definirá a própria senha ao acessar o <strong>link de ativação seguro</strong>. Ao salvar, você poderá copiar o link para enviar a ele.
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="modal-footer umu-wizard-footer" style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 20px 18px', borderTop: '1px solid var(--border-color, #e5e7eb)', flexShrink: 0 }}>
        {createFooter}
      </div>
    </Modal>
  );
}
